import classNames from 'classnames';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {getNid, WebUI} from "../../wui-core/src/index"
import Icon from '../../wui-icon/src';
import {addEventListener} from '../../wui-core/src/eventListener';
import ownerDocument from '../../wui-overlay/src/utils/ownerDocument';
import {isNumber} from './common/index.js';

import { DrawerProps } from './iDrawer'
import { WithConfigConsumer } from '../../wui-provider/src/context';

const defaultProps = {
    placement: 'right', // 抽屉的位置有四个预置的选项:left,right,top,bottom
    hasHeader: true, // 是否显示Drawer的头部信息
    show: false, // 是否显示抽屉组件
    showMask: true, // 是否显示遮罩
    maskClosable: true, // 点击遮罩是否可以关闭抽屉
    zIndex: 100, // 抽屉容器的层级，可以修改层级
    showClose: false, // 是否显示关闭按钮
    width: 'auto', // 抽屉的宽度
    height: 'auto', // 抽屉的高度
    destroyOnClose: false, // 关闭时候是否销毁抽屉的内容
    container: 'body', // 指定 Drawer 挂载的 HTML 节点
    closeIcon: null, // 自定义关闭图标
    title: '', // 抽屉的头部的标题
    className: '', // 抽屉容器的class, 用来自定义组件样式
    drawerStyle: {}, // 用于设置 Drawer 弹出层的样式
    footer: null, // 抽屉的页脚
    footerStyle: {}, // 抽屉页脚部件的样式
    style: {}, // 可用于设置 Drawer 最外层容器的样式，和drawerStyle的区别是作用节点包括mask
    maskStyle: {}, // 遮罩样式
    headerStyle: {}, // 用于设置 Drawer 头部的样式
    bodyStyle: {}, // 可用于设置 Drawer 内容部分的样式
    contentWrapperStyle: {}, // 可用于设置 Drawer 包裹内容部分的样式
    push: true,
    autoFocus: true,
    forceRender: false,
    keyboard: false
}

const formatDimension = (value: string | number | undefined, autoTo100: boolean): any => {
    if (isNumber(value)) return `${value}px`;
    if (typeof value === 'string') {
        if (value.endsWith('%')) return value;
        const parsed = parseInt(value);
        if (parsed > -1) return `${parsed}px`;
        if (value === 'auto' && autoTo100) return '100%';
    }
    return value;
};

const getDomNodeFromChildren = (children: React.ReactNodeArray, className: string, excludClassNames?: string[]) => {
    if (!children) return;
    const childrenArray = Array.isArray(children) ? children : [children];
    if (excludClassNames?.length) {
        return childrenArray.filter((child: React.ReactElement) => {
            const clsPrefix = child && child.props && child.props.clsPrefix ? child.props.clsPrefix : ''
            if (excludClassNames.every(className => !clsPrefix.includes(className))) {
                return true;
            }
            return false;
        })
    }
    return childrenArray.find((child: React.ReactElement) => {
        const clsPrefix = child && child.props && child.props.clsPrefix ? child.props.clsPrefix : ''
        if (!clsPrefix) return false;
        return clsPrefix.includes(className)
    })
};

const DrawerContext = React.createContext<any>(null);

const DrawerFunc: React.FC<DrawerProps> = (props) => {
    const {className, zIndex, style, forceRender, fieldid, destroyOnClose, showMask, clsPrefix, keyboard, autoFocus,
        maskClosable, maskStyle, showClose, closeIcon,
        hasHeader, title, children, width, height, placement, drawerStyle, footer, footerStyle, headerStyle, bodyStyle, extra,
        container, getContainer, getPopupContainer, show: propsShow = false, push: propsPush, dir: direction, onClose } = props;
    const [show, setShow] = useState(() => propsShow || false);
    const [pushFlag, setPushFlag] = useState(false);
    const [prevShow, setPrevShow] = useState(propsShow);

    const drawerRef = useRef<any>(null);
    const parentDrawerRef = useRef<{ push:() => void; pull: () => void } | null>(null);
    const onDocumentKeyupListenerRef = useRef<any>(null);
    const prevShowRef = useRef<boolean>();

    // 处理 getDerivedStateFromProps 逻辑
    useEffect(() => {
        if ('show' in props && propsShow !== prevShow) {
            setShow(propsShow || false);
            setPrevShow(propsShow);
        }
    }, [propsShow, prevShow]);

    // 处理键盘事件
    const handleDocumentKeyUp = useCallback((e: any) => {
        if (keyboard && e.keyCode === 27) {
            onClose && onClose();
        }
    }, [keyboard, onClose]);

    // componentDidMount 和 componentWillUnmount 逻辑
    useEffect(() => {
        prevShowRef.current = propsShow;
    }, [])

    useEffect(() => {
        const doc = ownerDocument(drawerRef.current);
        onDocumentKeyupListenerRef.current = addEventListener(doc, 'keyup', handleDocumentKeyUp);

        return () => {
            if (onDocumentKeyupListenerRef.current && typeof onDocumentKeyupListenerRef.current.remove === 'function') {
                onDocumentKeyupListenerRef.current.remove();
            }
        };
    }, [handleDocumentKeyUp]);

    // componentDidUpdate 逻辑 - 处理焦点和父级抽屉
    useEffect(() => {
        if (prevShowRef.current !== propsShow) {
            if (propsShow) {
                if (autoFocus) {
                    setTimeout(() => {
                        drawerRef.current?.focus?.();
                    }, 300)
                }
            }
            if (parentDrawerRef.current) {
                if (propsShow) {
                    parentDrawerRef.current.push();
                } else {
                    parentDrawerRef.current.pull();
                }
            }
        }
        prevShowRef.current = propsShow;
    }, [propsShow, autoFocus]);

    // push 和 pull 方法
    const push = useCallback(() => {
        setPushFlag(true);
    }, []);

    const pull = useCallback(() => {
        setPushFlag(false);
    }, []);

    // 将实例方法暴露给 DrawerContext
    const contextValue = useMemo(() => ({ push, pull }), [push, pull]);

    const fMaskClick = useCallback(() => {
        if (maskClosable) {
            if (onClose) {
                onClose();
            } else {
                setShow(false);
            }
        }
    }, [maskClosable, onClose]);

    const fCloseClick = useCallback(() => {
        if (onClose) {
            onClose();
        } else {
            setShow(false);
        }
    }, [onClose]);

    const renderMask = useCallback(() => {
        if (!showMask) return null;
        let maskStyleNode;
        if (show) {
            maskStyleNode = {
                opacity: 1,
                width: '100%',
                ...maskStyle
            }
        } else {
            maskStyleNode = {
                opacity: 0,
                width: 0
            }
        }
        return (<div className={`${clsPrefix}-mask`} style={maskStyleNode} onClick={fMaskClick} fieldid={fieldid ? fieldid + '-mask' : undefined} />)
    }, [showMask, clsPrefix, maskStyle, fieldid, show, fMaskClick]);

    const renderClose = useCallback(() => {
        if (!showClose) return null;
        let closeDom = closeIcon || <Icon type="uf-close"/>;
        return (<span className={`${clsPrefix}-close`} onClick={fCloseClick} fieldid={fieldid ? fieldid + '-close' : undefined}>{closeDom}</span>)
    }, [showClose, closeIcon, clsPrefix, fieldid, fCloseClick]);

    const renderBody = useCallback(() => {
        // 抽屉类
        const drawerClass = classNames(`${clsPrefix}`, `${clsPrefix}-${placement}`);
        // 根据位置获取抽屉样式
        const translateHideMap: Record<string, string> = {
            left: direction === 'rtl' ? 'translateX(100%)' : 'translateX(-100%)',
            right: direction === 'rtl' ? 'translateX(-100%)' : 'translateX(100%)',
            top: 'translateY(-100%)',
            bottom: 'translateY(100%)'
        };
        let translateShow = 'translate(0,0)';

        if (pushFlag) {
            let pushNum = 50;
            if (propsPush === true) {
                pushNum = 50;
            } else if (propsPush === false) {
                pushNum = 0
            } else if (typeof propsPush === 'object') {
                if (propsPush.distance) {
                    pushNum = parseFloat(propsPush.distance)
                } else {
                    pushNum = 50
                }
            }
            const translateShowMap: Record<string, string> = {
                left: `translate(${direction === 'rtl' ? -pushNum : pushNum}px,0)`,
                right: `translate(${direction === 'rtl' ? pushNum : -pushNum}px,0)`,
                top: `translate(0,${pushNum}px)`,
                bottom: `translate(0,-${pushNum}px)`
            }
            translateShow = translateShowMap[placement];
        }
        const translate = show ? translateShow : translateHideMap[placement];

        // 抽屉面板样式
        let isTopOrBottom = placement === 'top' || placement === 'bottom';
        let isLeftOrRight = placement === 'left' || placement === 'right';
        let stateWidth = formatDimension(width, isTopOrBottom);
        let stateHeight = formatDimension(height, isLeftOrRight);

        let showStatus = false
        if (!show && !destroyOnClose && isTopOrBottom) {
            stateWidth = 0
            showStatus = true
        }
        if (!show && !destroyOnClose && isLeftOrRight) {
            stateHeight = 0
            showStatus = true
        }
        if (show) {
            showStatus = true
        }
        const drawerStyleBody = {
            transform: translate,
            WebkitTransform: translate,
            width: stateWidth,
            height: stateHeight,
            ...drawerStyle
        }
        const closer = renderClose();
        const header = (
            hasHeader ? (<div className={`${clsPrefix}-header`}>
                <div className={`${clsPrefix}-header-title`}>{title}</div>
            </div>) : null
        )
        const headerBoxClass = classNames({
            [`${clsPrefix}-header-box`]: true,
            [`${clsPrefix}-header-box-ishead`]: !hasHeader,
        });

        return (
            <DrawerContext.Provider value={contextValue}>
                {showStatus && (
                    <div ref={(drawer) => {
                        drawerRef.current = drawer
                    }} tabIndex={-1} className={drawerClass} style={drawerStyleBody}>
                        <div className={headerBoxClass} style={headerStyle}>
                            {header}
                            {extra && <div className={`${clsPrefix}-extra`} style={{ right: showClose ? 40 : 20 }}>{extra}</div>}
                            {closer}
                        </div>
                        <div className={`${clsPrefix}-body`} style={bodyStyle}>
                            {getDomNodeFromChildren((children as React.ReactNodeArray), ``, [`${clsPrefix}-header`, `${clsPrefix}-footer`])}
                        </div>
                        {footer ? (
                            <div className={`${clsPrefix}-footer`} style={footerStyle}>
                                {footer}
                            </div>
                        ) : getDomNodeFromChildren((children as React.ReactNodeArray), `${clsPrefix}-footer`)
                        }
                    </div>
                )}
            </DrawerContext.Provider>
        )
    }, [destroyOnClose, clsPrefix, hasHeader, title, children, width, height, placement, drawerStyle, footer, footerStyle, headerStyle, bodyStyle, propsPush, extra, direction, show, pushFlag, renderClose]);

    const renderAll = useCallback((value: any) => {
        if (!forceRender && destroyOnClose && !show) {
            return null
        }
        // 容器类
        const drawercClass = classNames({
            [`drawerc`]: true,
            [`${clsPrefix}-hidden-mask`]: !showMask,
            [`${className}`]: className
        });
        // 容器样式
        let drawercStyle: {zIndex: number | undefined, width: string | number} = {zIndex: zIndex, width: 0}
        if (show) {
            drawercStyle.width = '100%';
        } else {
            drawercStyle.width = 0;
        }
        Object.assign(drawercStyle, style)
        // 获取父级抽屉
        parentDrawerRef.current = value;
        let adapterNid = getNid(props) // 适配nid、uitype

        return (
            <div className={drawercClass} style={drawercStyle} {...adapterNid} fieldid={fieldid}>
                {renderMask()}
                {renderBody()}
            </div>
        )
    }, [className, zIndex, style, forceRender, fieldid, destroyOnClose, showMask, clsPrefix, show, renderMask, renderBody]);

    // const getParent = useCallback(() => {
    //     const containerDom = getPopupContainer || getContainer || container;
    //     if (typeof containerDom === 'string') {
    //         return document.querySelector(containerDom)
    //     }
    //     if (typeof containerDom === 'function') {
    //         let returnData = (containerDom as () => HTMLElement)()
    //         if (typeof returnData === 'string') {
    //             return document.querySelector(returnData)
    //         } else if (typeof returnData === 'object' && (returnData as HTMLElement) instanceof window.HTMLElement) {
    //             return returnData
    //         } else if (typeof returnData === 'object' || typeof returnData === 'number' || typeof returnData === 'boolean' || typeof returnData === undefined) {
    //             return document.querySelector('body')
    //         }
    //     }
    //     if (typeof containerDom === 'object' && (containerDom as HTMLElement) instanceof window.HTMLElement) {
    //         return containerDom
    //     }
    //     return document.querySelector('body')
    // }, [container, getContainer, getPopupContainer]);

    const getParent = useCallback(() => {
        const containerDom = getPopupContainer || getContainer || container;
        // 直接返回 HTMLElement
        if (containerDom instanceof HTMLElement) {
            return containerDom;
        }
        // 处理字符串选择器
        if (typeof containerDom === 'string') {
            return document.querySelector(containerDom) || document.body;
        }
        // 处理函数
        if (typeof containerDom === 'function') {
            const result = containerDom();
            if (result instanceof HTMLElement) {
                return result;
            }
            if (typeof result === 'string') {
                return document.querySelector(result) || document.body;
            }
        }
        // 默认返回 body
        return document.body;
    }, [container, getContainer, getPopupContainer]);

    // render 部分
    const conDom = useMemo(() => getParent(), [getParent]);

    return (
        ReactDOM.createPortal(<DrawerContext.Consumer>{renderAll}</DrawerContext.Consumer>, conDom as HTMLElement)
    )
};

// 应用装饰器
export default WithConfigConsumer({name: "drawer"})(
    WebUI({name: "drawer", defaultProps})(DrawerFunc)
);